import { logUserAction } from "@/apis/logs/route";

/*
 * 홈 개편 뒤 사용자가 기능을 어떻게 찾아 쓰는지 보는 행동 로그. Slack 으로는 보내지 않고
 * 서버 로그(→ BigQuery)에만 남는다.
 *
 * WHY: 행동 이름의 괄호 앞부분이 BigQuery 집계의 키이고, 괄호 안은 "진입점: 대상"이다.
 * 집계 쿼리가 이 문구에 기대므로 이름을 바꾸면 이전 기록과 키가 갈린다. 그래서 문구를
 * 호출부마다 쓰지 않고 이 파일에만 둔다.
 */

/** 노션 가이드로 가는 클릭. `entry` 는 어디서 눌렀는지다 */
export const logUserGuideOpen = (entry: string) =>
  logQuietly(`이용가이드 열기 (${entry})`);

/** 가이드 영상(유튜브)으로 가는 클릭. 노션 가이드와 따로 세도록 행동 이름을 나눈다 */
export const logGuideVideoOpen = (entry: string) =>
  logQuietly(`가이드 영상 열기 (${entry})`);

export const logAiAskOpen = () => logQuietly("AI 물어보기 열기");

export const logAiAskQuestion = (question: string) =>
  logQuietly(`AI 물어보기 질문 (${question})`);

/**
 * 홈에서 기능으로 들어간 길. `entry` 는 STEP 카드·AI 물어보기·사이드바·단계 패널 중 어디서
 * 눌렀는지, `target` 은 들어간 기능이다.
 */
export const logHomeServiceAccess = (entry: string, target: string) =>
  logQuietly(`홈에서 서비스 접근 (${entry}: ${target})`);

/** 홈 단계 패널 안에서 바로 시작한 작업. 패널을 떠나 다른 화면으로 가는 버튼은 logHomeServiceAccess 다 */
export const logHomeWork = (step: string, action: string) =>
  logQuietly(`홈에서 작업 (${step}: ${action})`);

/** 인벡터 AI 상담(/help)을 연 클릭 */
export const logInvectorAiOpen = (entry: string) =>
  logQuietly(`인벡터 AI 열기 (${entry})`);

/**
 * 인벡터 AI 상담에 보낸 질문.
 *
 * WHY: 답을 만드는 서버(invector-cs-rag)는 질문을 저장하지 않아서, 무엇을 물었는지는 여기서만 남는다.
 */
export const logInvectorAiQuestion = (question: string) =>
  logQuietly(`인벡터 AI 질문 (${question})`);

/**
 * 우리 서버를 거치지 않는 외부 서비스를 연 클릭.
 *
 * WHY: 외부 서비스 사용은 우리 로그에 남지 않아서, 쓴 흔적이 이 클릭뿐이다.
 */
export const logExternalServiceOpen = (service: string) =>
  logQuietly(`외부 서비스 열기 (${service})`);

export const logContactOpen = () => logQuietly("문의하기 열기");

export const logSolutionTabChange = (solution: string) =>
  logQuietly(`사이드바 솔루션 탭 (${solution})`);

/** 제출용 IXD 를 편집용으로 바꾸는 방법 안내를 연 클릭. `entry` 는 누른 화면의 경로다 */
export const logIxdConversionGuideOpen = (entry: string) =>
  logQuietly(`편집용 파일 변환 방법보기 (${entry})`);

/** 제출용 IXD 를 올려 변환 안내 모달이 뜬 순간. 변환 방법보기 클릭 직전에 무엇을 겪었는지 본다 */
export const logIxdSubmissionUploadRejected = (entry: string) =>
  logQuietly(`제출용 IXD 업로드 거절 (${entry})`);

// 문구가 집계 키라 호출부에서 자유 문자열을 받지 않고 여기 목록으로 묶는다
type IxdEditorAction =
  // 진입 화면 (/ixd-editor)
  | "새 파일 업로드 탭"
  | "이전 파일 불러오기 탭"
  | "XBRL 공시본 열람 탭"
  // 편집 화면 (/ixd-editor/view)
  | "페이지 진입"
  | "목차 접기"
  | "목차 펼치기"
  | "목차 이동"
  | "연결 보기"
  | "별도 보기"
  | "워크북 값 불러오기"
  | "워크북 변경"
  | "워크북 선택 완료"
  | "DSD워크북 보기 켜기"
  | "DSD워크북 보기 끄기"
  | "값 반영"
  | "DSD 원본보기 켜기"
  | "DSD 원본보기 끄기"
  | "합계 자동계산 켜기"
  | "합계 자동계산 끄기"
  | "되돌리기"
  | "전체 초기화"
  | "전체 초기화 확정"
  | "저장"
  | "저장 확정"
  | "저장 거절 안내 열림"
  | "저장 거절 안내 이전"
  | "저장 거절 안내 다음"
  | "저장 거절 안내 닫기"
  | "IXD로 다운로드"
  | "로고 클릭으로 나가기"
  // 다운로드 완료 패널. 닫기·나중에 하기는 세지 않는다 — 열림 뒤에 최종 검토가 없으면 미룬 것이다
  | "다운로드 완료 패널 열림"
  | "다시 다운로드"
  | "최종 검토 하러가기";

/**
 * 편집기가 URL 에 적어 두는 선택 상태(view-url.ts·use-url-workbook.ts 의 파라미터).
 * 이름이 바뀌면 그 값만 로그에서 빠지고 화면 동작에는 영향이 없다.
 */
const EDITOR_URL_LOG_PARAMS = [
  "reportId",
  "fsType",
  "consolidatedWorkbookId",
  "separateWorkbookId",
  "workbookId",
];

type LogIds = Record<string, string | number | boolean | undefined>;

/**
 * IXD 값 편집기 안의 행동. 진입 → 워크북 불러오기 → 값 반영 → 저장·다운로드 → 최종 검토
 * 퍼널을 사람·시각 단위로 본다 (예: 진입 후 얼마 만에 워크북 값 불러오기를 눌렀는지).
 *
 * 문구 뒤에 어떤 문서·워크북·표였는지를 JSON 으로 붙인다 — 예:
 * `IXD 값 편집기 (값 반영) {"reportId":"…","fsType":"consolidated","consolidatedWorkbookId":"…","workbookTableId":"…"}`.
 * 문서·워크북은 클릭 순간의 URL 에서 읽고, URL 에 없는 표 id 등은 호출부가 `ids` 로 넘긴다.
 * 집계 키(괄호까지)는 그대로라 기존 집계와 갈리지 않는다.
 */
export const logIxdEditorAction = (
  action: IxdEditorAction,
  ids: LogIds = {},
) => {
  const params = new URLSearchParams(window.location.search);
  const context: LogIds = {};
  for (const name of EDITOR_URL_LOG_PARAMS) {
    const value = params.get(name);
    if (value) context[name] = value;
  }
  for (const [name, value] of Object.entries(ids)) {
    if (value !== undefined) context[name] = value;
  }
  const detail =
    Object.keys(context).length > 0 ? ` ${JSON.stringify(context)}` : "";
  return logQuietly(`IXD 값 편집기 (${action})${detail}`);
};

/**
 * IXD-DSD 대사 화면의 「IXD 위치 확인」 사용. `action` 은 다음·이전·이 줄 값 보기·도움말·
 * 안내 닫기 중 하나다.
 *
 * WHY: 전부 화면 안에서 끝나는 동작이라 서버에 다른 흔적이 없다 — 이 로그가 유일한 기록이다.
 */
export const logPositionCheck = (action: string) =>
  logQuietly(`대사 위치 확인 (${action})`);

function logQuietly(actionLabel: string) {
  return logUserAction(actionLabel, [], { notify: false });
}
